import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';

import { ShipmentModel, ValidationMessageModel } from '@data-layer/models/common';
import { DocumentCreateModel, DocumentModel } from '@data-layer/models/documents';
import {
  enumDocumentRelatedEntityType,
  enumDocumentRootEntityType,
  enumDocumentState,
  enumDocumentType,
} from '@data-layer/enums/documents';
import { Observable } from 'rxjs';
import { IEsDocumentStateUpdate } from '../../interfaces';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'es-bill-lading-attach-document',
  templateUrl: './es-bill-lading-attach-document.component.html',
  styleUrls: ['./es-bill-lading-attach-document.component.scss'],
})
export class EsBillLadingAttachDocumentComponent implements OnChanges {
  @Input() public entrySummaryId: number;
  @Input() public isTruck: number;
  @Input() public shipment: ShipmentModel;
  @Input() public attachedDocuments: DocumentModel[] = [];
  @Input() public isCanUploadDocuments: boolean = false;
  @Input() public isCanRemoveDocuments: boolean = false;
  @Input() public isShowEmptyLineItemDocuments: boolean = true;
  @Input() public validationMessages: ValidationMessageModel[] = [];
  @Input() public currentShipmentIndex: number = 1;
  @Input() public upload: (document: DocumentCreateModel, file: File) => Observable<any>;
  @Input() public remove: (documentId: number) => Observable<any>;
  @Input() public billName: string = 'DOCUMENT_TYPE.BILL_OF_LADING';
  @Output() public stateUpdate: EventEmitter<IEsDocumentStateUpdate> = new EventEmitter<IEsDocumentStateUpdate>();

  public enumDocumentType: typeof enumDocumentType = enumDocumentType;
  public shipmentDocument: DocumentModel;
  public otherDocuments: DocumentModel[];
  public lineItemDocuments: DocumentModel[] = [];
  public createDocumentTemplate: DocumentCreateModel;

  constructor(private translateService: TranslateService) {}

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.attachedDocuments) {
      this.prepareDocuments();
    }

    this.createDocumentTemplate = {
      relatedEntityType: enumDocumentRelatedEntityType.SHIPMENT,
      relatedEntityId: this.shipment.id,
      rootEntityType: enumDocumentRootEntityType.ENTRY_SUMMARY,
      rootEntityId: this.entrySummaryId,
      fileName: null,
      type: null,
    };
  }

  public get shipmentName(): string {
    return this.shipment && this.shipment.masterBill && this.shipment.masterBill.carrier && this.shipment.masterBill.number
      ? this.shipment.masterBill.carrier + this.shipment.masterBill.number
      : this.translateService.instant('DOCUMENT_TYPE.BILL_OF_LADING') + ' #' + this.currentShipmentIndex;
  }

  public onStateUpdate(data: IEsDocumentStateUpdate) {
    this.stateUpdate.emit(data);
  }

  private prepareDocuments() {
    this.updateShipmentDocument();
    this.updateOtherDocuments();
    this.lineItemDocuments = this.isShowEmptyLineItemDocuments ? [] : this.buildLinesDocuments();
  }

  private updateShipmentDocument() {
    const shipmentDocument = this.findAttachedDocument(
      enumDocumentRelatedEntityType.SHIPMENT,
      enumDocumentType.BILL_OF_LADING,
      this.shipment.id,
    ) || {
      relatedEntityId: this.shipment.id,
      type: enumDocumentType.BILL_OF_LADING,
      relatedEntityType: enumDocumentRelatedEntityType.SHIPMENT,
      rootEntityType: enumDocumentRootEntityType.ENTRY_SUMMARY,
      rootEntityId: this.entrySummaryId,
      id: null,
      createdDate: null,
      url: null,
      fileName: null,
      state: enumDocumentState.CREATED,
    };
    this.shipmentDocument = this.mergeDocument(this.shipmentDocument, shipmentDocument);
  }

  private updateOtherDocuments() {
    this.otherDocuments = this.findOtherDocument(this.shipment.id);
  }

  private findOtherDocument(objectId: number) {
    const otherUploadedDocuments: DocumentModel[] = this.attachedDocuments.filter(
      (document) =>
        document.relatedEntityType === enumDocumentRelatedEntityType.SHIPMENT &&
        [
          enumDocumentType.PERMIT,
          enumDocumentType.ARRIVAL_NOTICE,
          enumDocumentType.TRADE_CERTIFICATE,
          enumDocumentType.UNKNOWN,
          enumDocumentType.PACKING_LIST,
        ].includes(document.type) &&
        document.relatedEntityId === objectId,
    );

    const arrivalNotice: DocumentModel = otherUploadedDocuments.find((document) => document.type === enumDocumentType.ARRIVAL_NOTICE) || {
      relatedEntityId: this.shipment.id,
      type: enumDocumentType.ARRIVAL_NOTICE,
      relatedEntityType: enumDocumentRelatedEntityType.SHIPMENT,
      rootEntityType: enumDocumentRootEntityType.ENTRY_SUMMARY,
      rootEntityId: this.entrySummaryId,
      url: null,
      createdDate: null,
      id: null,
      fileName: null,
      state: enumDocumentState.CREATED,
    };

    const packingList: DocumentModel = otherUploadedDocuments.find((document) => document.type === enumDocumentType.PACKING_LIST) || {
      relatedEntityId: this.shipment.id,
      type: enumDocumentType.PACKING_LIST,
      relatedEntityType: enumDocumentRelatedEntityType.SHIPMENT,
      rootEntityType: enumDocumentRootEntityType.ENTRY_SUMMARY,
      rootEntityId: this.entrySummaryId,
      id: null,
      createdDate: null,
      url: null,
      fileName: null,
      state: enumDocumentState.CREATED,
    };

    return [
      this.otherDocuments && this.otherDocuments.length > 0 ? this.mergeDocument(this.otherDocuments[0], arrivalNotice) : arrivalNotice,
      this.otherDocuments && this.otherDocuments.length > 1 ? this.mergeDocument(this.otherDocuments[1], packingList) : packingList,
      ...otherUploadedDocuments.filter((document) => document.id !== arrivalNotice.id && document.id !== packingList.id),
    ];
  }

  private findAttachedDocument(objectType: enumDocumentRelatedEntityType, type: enumDocumentType, objectId: number) {
    return this.attachedDocuments.find(
      (document) => document.relatedEntityType === objectType && document.type === type && document.relatedEntityId === objectId,
    );
  }

  private buildLinesDocuments(): DocumentModel[] {
    const result: DocumentModel[] = [];
    for (let i = 0; i < this.shipment.invoices.length; i++) {
      const invoice = this.shipment.invoices[i];
      for (let j = 0; j < invoice.lines.length; j++) {
        const line = invoice.lines[j];
        const document = this.findAttachedDocument(enumDocumentRelatedEntityType.LINE_ITEM, enumDocumentType.UNKNOWN, line.id);
        if (!document) {
          const invoiceName: string = invoice.invoiceNumber ? ' ' + invoice.invoiceNumber : '';
          result.push({
            type: enumDocumentType.UNKNOWN,
            rootEntityId: this.entrySummaryId,
            rootEntityType: enumDocumentRootEntityType.ENTRY_SUMMARY,
            relatedEntityId: line.id,
            relatedEntityType: enumDocumentRelatedEntityType.LINE_ITEM,
            id: null,
            fileName: 'Invoice ' + '#' + (i + 1) + invoiceName + ' Line #' + (j + 1) + ' ' + line.htsNumber,
            url: null,
            state: enumDocumentState.CREATED,
          });
        }
      }
    }
    return result;
  }

  private mergeDocument(existedDocument: DocumentModel, newDocument: DocumentModel): DocumentModel {
    if (!existedDocument) {
      return newDocument;
    }
    Object.assign(existedDocument, newDocument);
    return existedDocument;
  }
}
